import styles from './Parameter.module.scss';
import TextInput from 'common/components/textInput/TextInput';
import { Label } from 'common/components/texts/Texts';
import { useTranslation } from 'react-i18next';
import SelectInput, { defaultSelectInputStyles } from 'common/components/selectInput/SelectInput';
import { AnimateLayoutChanges, useSortable } from '@dnd-kit/sortable';
import { CSSProperties, forwardRef, Ref, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CSS } from '@dnd-kit/utilities';
import CheckInput from 'common/components/checkInput/CheckInput';
import { OptionTitleSubTitle, SearchableSelectTitleSubTitleOption } from 'common/components/searchableSelectInput/optionsFormats/OptionTitleSubTitle';
import { Col, Row } from 'react-bootstrap';
import { ParameterSectionBaseModel, ParameterSectionParameterType, ParameterSectionType } from 'common/components/parametersSections/models';
import { FamilyDto } from 'api/families/models/FamilyDto';
import { BODY_TOOLTIP } from 'Config';
import InputError from 'common/components/inputError/InputError';
import ExpressionSystem, { ExpressionParameter } from 'common/components/expressionButton/ExpressionSystem';
import { FamilyParameterDataProvider } from './FamilyParameterDataProvider';
import type { CatalogParameterDto } from 'api/catalogParameters/models/CatalogParameterDto';
import CreateCatalogParameterModal from 'screens/library/catalogParameters/createModal/CreateCatalogParameterModal';
import { IoAddSharp } from 'react-icons/io5';
import InfoIcon from 'common/components/infoIcon/InfoIcon';
import InformationModal from 'common/components/modal/informationModal/InformationModal';
import CreatableSelect from 'react-select/creatable';
import { components, createFilter, GroupBase, MenuListProps } from 'react-select';

const animateLayoutChanges: AnimateLayoutChanges = ({ isSorting, wasDragging }) =>
    !(isSorting || wasDragging);

const getSectionPath = (param: ParameterSectionBaseModel, allParams: ParameterSectionBaseModel[]): string[] => {
    const path: string[] = [];
    let currentKey = param.parentKey;
    while (currentKey) {
        const parent = allParams.find(p => p.key === currentKey);
        if (!parent) break;
        if (parent.title) path.unshift(parent.title);
        currentKey = parent.parentKey;
    }
    return path;
};

const BUDGET_FIELD_OPTIONS: { fieldKey: string; translationKey: string; valueType: 'number' | 'string' }[] = [
    { fieldKey: 'quantity', translationKey: 'budgets.budget.lines.header.quantity_tooltip', valueType: 'number' },
    { fieldKey: 'grossUnitPrice', translationKey: 'budgets.budget.lines.header.gross_unit_price', valueType: 'number' },
    { fieldKey: 'marginPercentage', translationKey: 'budgets.budget.lines.header.margin', valueType: 'number' },
    { fieldKey: 'discountPercentage', translationKey: 'budgets.budget.lines.header.discount', valueType: 'number' },
    { fieldKey: 'unitPrice', translationKey: 'budgets.budget.lines.header.price_per_unity_tooltip', valueType: 'number' },
    { fieldKey: 'totalUnitExcludingTax', translationKey: 'budgets.budget.lines.header.total_unit_without_tax_tooltip', valueType: 'number' },
    { fieldKey: 'unitPriceWithChildren', translationKey: 'budgets.budget.lines.header.unit_price_with_children', valueType: 'number' },
    { fieldKey: 'absoluteChildrenSum', translationKey: 'budgets.budget.lines.header.absolute_children_sum', valueType: 'number' },
    { fieldKey: 'totalExcludingTax', translationKey: 'budgets.budget.lines.header.total_without_tax', valueType: 'number' },
    { fieldKey: 'totalIncludingTax', translationKey: 'budgets.budget.lines.header.total_with_tax', valueType: 'number' },
    { fieldKey: 'unitSymbol', translationKey: 'budgets.budget.lines.header.unity_tooltip', valueType: 'string' },
];

const getFamilyFormulaParameters = (
    allParameters: ParameterSectionBaseModel[],
    currentKey: string,
    includeArrayOptions?: boolean,
    listOfLabel?: string,
    t?: (key: string) => string,
    catalogParameters?: CatalogParameterDto[]
): ExpressionParameter[] => {
    const filteredParams = (allParameters || [])
        .filter(p =>
            p.key !== currentKey &&
            p.type === ParameterSectionType.PARAMETER &&
            ['NUMBER', 'QUANTITY', 'PRICE', 'NUMBER_MIN_MAX', 'WEIGHT'].includes(p.parameterType ?? '')
        );

    let result: ExpressionParameter[];

    if (includeArrayOptions && listOfLabel) {
        // Intercalar: campo1, arrayof_campo1, campo2, arrayof_campo2, ...
        result = filteredParams.flatMap(p => {
            const sectionPath = getSectionPath(p, allParameters);
            return [
                {
                    // Use p.id (GUID) for existing params, fallback to p.key for new params
                    id: ExpressionSystem.formatParameterId([p.id || p.key]),
                    name: ExpressionSystem.formatParameterName([...sectionPath, p.parameterName || '']),
                    value: 0
                },
                {
                    id: ExpressionSystem.formatParameterId(['LISTOF', p.id || p.key]),
                    name: ExpressionSystem.formatParameterName([listOfLabel, ...sectionPath, p.parameterName || '']),
                    value: 0
                }
            ];
        });
    } else {
        result = filteredParams.map(p => {
            const sectionPath = getSectionPath(p, allParameters);
            return {
                id: ExpressionSystem.formatParameterId([p.id || p.key]),
                name: ExpressionSystem.formatParameterName([...sectionPath, p.parameterName || '']),
                value: 0
            };
        });
    }

    // Also register catalog parameter IDs (and LISTOF variants) so formulas imported from catalog resolve correctly
    for (const p of filteredParams) {
        if (p.catalogParameterId) {
            const sectionPath = getSectionPath(p, allParameters);
            const catalogId = ExpressionSystem.formatParameterId([p.catalogParameterId]);
            if (!result.some(r => r.id === catalogId)) {
                result.push({
                    id: catalogId,
                    name: ExpressionSystem.formatParameterName([...sectionPath, p.parameterName || '']),
                    value: 0,
                    hidden: true
                });
            }
            const catalogListOfId = ExpressionSystem.formatParameterId(['LISTOF', p.catalogParameterId]);
            if (!result.some(r => r.id === catalogListOfId)) {
                result.push({
                    id: catalogListOfId,
                    name: ExpressionSystem.formatParameterName([listOfLabel || '', ...sectionPath, p.parameterName || '']),
                    value: 0,
                    hidden: true
                });
            }
        }
    }

    // Append parent line catalog parameter options
    if (t && catalogParameters && catalogParameters.length > 0) {
        const budgetLabel = t('budgets.budget.budget');
        const parentLineLabel = t('catalog_parameters.parent_line_label');
        const numericTypes = ['NUMBER', 'QUANTITY', 'PRICE', 'NUMBER_MIN_MAX', 'WEIGHT'];
        catalogParameters
            .filter(cp => cp.parameterType && numericTypes.includes(cp.parameterType))
            .forEach(cp => {
                result.push({
                    id: ExpressionSystem.formatParameterId(['CURRENT_PARENT_LINE', 'CATALOG', cp.id, 'parameter']),
                    name: ExpressionSystem.formatParameterName([budgetLabel, parentLineLabel, cp.name]),
                    value: 0,
                });
            });
    }

    // Append composition (PRODUCTS) parameter pricing fields
    if (t) {
        const productsParams = (allParameters || [])
            .filter(p =>
                p.key !== currentKey &&
                p.type === ParameterSectionType.PARAMETER &&
                p.parameterType === ParameterSectionParameterType.PRODUCTS
            );
        const compositionFields: { fieldKey: string; translationKey: string }[] = [
            { fieldKey: 'totalExcludingTax', translationKey: 'budgets.budget.lines.header.total_without_tax' },
            { fieldKey: 'totalIncludingTax', translationKey: 'budgets.budget.lines.header.total_with_tax' },
        ];
        for (const p of productsParams) {
            const sectionPath = getSectionPath(p, allParameters);
            for (const field of compositionFields) {
                result.push({
                    id: ExpressionSystem.formatParameterId([p.id || p.key, 'composition', field.fieldKey]),
                    name: ExpressionSystem.formatParameterName([...sectionPath, p.parameterName || '', t(field.translationKey)]),
                    value: 0,
                });
            }
        }
    }

    // Append fixed budget field options:
    // - "Orçamento > Linha atual > Campo" → value from the current element line
    // - "Orçamento > Secção > Campo" → value from ancestor (top-down, first filled)
    if (t) {
        const budgetLabel = t('budgets.budget.budget');
        const currentLineLabel = t('catalog_parameters.current_line_label');
        const sectionLabel = t('budgets.budget.lines.add_options.section');
        for (const field of BUDGET_FIELD_OPTIONS) {
            const fieldLabel = t(field.translationKey);
            const defaultValue = field.valueType === 'string' ? '' : 0;
            result.push({
                id: ExpressionSystem.formatParameterId(['BUDGET_FIELD_ELEMENT', field.fieldKey]),
                name: ExpressionSystem.formatParameterName([budgetLabel, currentLineLabel, fieldLabel]),
                value: defaultValue,
            });
            result.push({
                id: ExpressionSystem.formatParameterId(['BUDGET_FIELD_SECTION', field.fieldKey]),
                name: ExpressionSystem.formatParameterName([budgetLabel, sectionLabel, fieldLabel]),
                value: defaultValue,
            });
        }

        // - "Orçamento > Linha pai > Campo" → value from the nearest element/component ancestor
        const parentLineLabel = t('catalog_parameters.parent_line_label');
        for (const field of BUDGET_FIELD_OPTIONS) {
            if (field.valueType === 'string') continue;
            const fieldLabel = t(field.translationKey);
            result.push({
                id: ExpressionSystem.formatParameterId(['BUDGET_FIELD_PARENT', field.fieldKey]),
                name: ExpressionSystem.formatParameterName([budgetLabel, parentLineLabel, fieldLabel]),
                value: 0,
            });
        }
    }

    return result;
};

const buildFamilyCircularReferenceValidator = (
    allParameters: ParameterSectionBaseModel[],
    currentKey: string,
    t: (key: string) => string
): ((expression: string) => string | null) => {
    return (expression: string) => {
        const currentParam = allParameters.find(p => p.key === currentKey);
        if (!currentParam) return null;

        const currentId = ExpressionSystem.formatParameterId([currentParam.id || currentParam.key]);
        const currentMentionId = ExpressionSystem.serializeParameterMentionId(currentId);

        const formulasMap = new Map<string, string | null>();
        allParameters
            .filter(p =>
                p.key !== currentKey &&
                p.type === ParameterSectionType.PARAMETER &&
                ['NUMBER', 'QUANTITY', 'PRICE', 'NUMBER_MIN_MAX', 'WEIGHT'].includes(p.parameterType ?? '')
            )
            .forEach(p => {
                const mentionId = ExpressionSystem.serializeParameterMentionId(
                    ExpressionSystem.formatParameterId([p.id || p.key])
                );
                formulasMap.set(mentionId, p.formulaExpression ?? null);
                // Also add LISTOF variant since formulas can reference array versions
                const listOfMentionId = ExpressionSystem.serializeParameterMentionId(
                    ExpressionSystem.formatParameterId(['LISTOF', p.id || p.key])
                );
                formulasMap.set(listOfMentionId, p.formulaExpression ?? null);
            });

        if (ExpressionSystem.hasCircularReference(currentMentionId, expression, formulasMap)) {
            return t('common.formula_circular_reference');
        }
        return null;
    };
};

export interface ParameterSectionProps {
    parameter: ParameterSectionBaseModel;
    disabled?: boolean;
    unitOptions?: SearchableSelectTitleSubTitleOption[];
    selectInputOptions?: SearchableSelectTitleSubTitleOption[];
    onChange: (parameter: ParameterSectionBaseModel) => void;
    family?: Partial<FamilyDto>;
    allParameters?: ParameterSectionBaseModel[];
}

const Parameter = ({ parameter, disabled, unitOptions, selectInputOptions, onChange, family, allParameters }: ParameterSectionProps, ref: Ref<HTMLDivElement>): JSX.Element | null => {
    const { t } = useTranslation();
    const { weightUnitId, catalogParameters, onCatalogParameterCreated } = useContext(FamilyParameterDataProvider);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDiscountInfo, setShowDiscountInfo] = useState(false);
    const [showTreatmentInfo, setShowTreatmentInfo] = useState(false);

    // Exclude catalog parameters already used in this family (except the current one)
    const usedCatalogIds = new Set(
        (allParameters || [])
            .filter(p => p.catalogParameterId && p.key !== parameter.key)
            .map(p => p.catalogParameterId)
    );
    const catalogParamOptions = useMemo(() => {
        const opts = (catalogParameters || [])
            .filter(cp => !usedCatalogIds.has(cp.id))
            .map(cp => ({
                label: cp.name,
                value: cp.id,
                parameterType: cp.parameterType ? ` (${t('common.parameters_sections.parameters_types.' + cp.parameterType)})` : '',
            }));
        if (!disabled) {
            opts.push({ label: t('common.new'), value: '__create_new__', parameterType: '' });
        }
        return opts;
    }, [catalogParameters, usedCatalogIds, t, disabled]);

    const [nameInputValue, setNameInputValue] = useState('');

    // Current value for the CreatableSelect: either a catalog option or a free-text entry
    const nameSelectValue = useMemo(() => {
        if (parameter.catalogParameterId) {
            const found = catalogParamOptions.find(o => o.value === parameter.catalogParameterId);
            if (found) return found;
        }
        if (parameter.parameterName) {
            return { label: parameter.parameterName, value: '__custom__', parameterType: '' };
        }
        return null;
    }, [parameter.catalogParameterId, parameter.parameterName, catalogParamOptions]);

    const options = Object.keys(ParameterSectionParameterType)
        .filter(key => key !== ParameterSectionParameterType.NUMBER_MIN_MAX)
        .map(key => ({
            label: t('common.parameters_sections.parameters_types.' + key),
            value: key,
        })).sort((a, b) => a.label.localeCompare(b.label));

    const [valueHasError, setValueHasError] = useState<boolean>(false);

    // const {
    //     setNodeRef,
    //     transform,
    //     transition,
    //     isDragging,
    // } = useSortable({
    //     id: parameter.key,
    //     animateLayoutChanges,
    //     disabled,
    // });

    // const style: CSSProperties = {
    //     transform: CSS.Translate.toString(transform),
    //     transition,
    //     opacity: isDragging ? 0.3 : 1,
    // };

    // useEffect(() => {
    //     if (family?.isComponent && !parameter.parameterType) {
    //         const newParameterType = ParameterSectionParameterType.PRODUCTS;
    //         onChange({ ...parameter, parameterType: newParameterType });
    //     }

    //     if (family?.isComponent && parameter.parameterType && parameter.parameterType !== ParameterSectionParameterType.PRODUCTS) {
    //         setValueHasError(true);
    //     } else {
    //         setValueHasError(false);
    //     }
    // }, [family?.isComponent, family?.isElement, parameter.parameterType]);

    return (
        <div
            ref={ref}
            // style={style}
            className={styles.container}
        >
            <Row className={styles.row}>
                <Col xs={12} md={12} lg={6} xl={4} xxl={3} xxxl={2}>
                    <div className={styles.control}>
                        <Label space>{t('common.parameters_sections.parameter_name')}</Label>
                        <CreatableSelect
                            value={nameSelectValue}
                            options={catalogParamOptions}
                            isDisabled={disabled}
                            onChange={(option) => {
                                if (!option) {
                                    onChange({ ...parameter, catalogParameterId: null, parameterName: '' });
                                    return;
                                }
                                if (option.value === '__custom__') {
                                    return;
                                }
                                if (option.value === '__create_new__') {
                                    setShowCreateModal(true);
                                    return;
                                }
                                const cp = (catalogParameters || []).find(c => c.id === option.value);
                                if (cp) {
                                    onChange({
                                        ...parameter,
                                        catalogParameterId: cp.id,
                                        parameterType: (cp.parameterType as ParameterSectionParameterType) ?? parameter.parameterType,
                                        parameterName: cp.name ?? parameter.parameterName,
                                        formulaExpression: cp.formulaExpression ?? parameter.formulaExpression,
                                        unitId: cp.unitId ?? parameter.unitId,
                                    });
                                }
                            }}
                            onCreateOption={(inputValue) => {
                                onChange({ ...parameter, catalogParameterId: null, parameterName: inputValue });
                            }}
                            formatOptionLabel={(option, { context }) => {
                                if (context === 'menu' && option.value === '__create_new__') {
                                    return <span className={styles.createNewOption}><IoAddSharp /> {option.label}</span>;
                                }
                                if (context === 'menu' && option.parameterType) {
                                    return <span>{option.label}<span style={{ color: '#A5ADC6', fontSize: '0.85em' }}>{option.parameterType}</span></span>;
                                }
                                return option.label;
                            }}
                            formatCreateLabel={(inputValue) => inputValue}
                            createOptionPosition="first"
                            isValidNewOption={(inputValue) => inputValue.trim().length > 0}
                            placeholder={t('common.write_here')}
                            isClearable={false}
                            menuPortalTarget={document.body}
                            menuPlacement="auto"
                            menuPosition="fixed"
                            styles={defaultSelectInputStyles(disabled ?? false, false, false, false)}
                            filterOption={(option, inputValue) => {
                                if (option.data.value === '__create_new__') return true;
                                return createFilter({
                                    ignoreCase: true,
                                    ignoreAccents: true,
                                    trim: true,
                                    matchFrom: 'any' as const,
                                    stringify: (o) => o.label,
                                })(option, inputValue);
                            }}
                            noOptionsMessage={() => t('common.no_options')}
                            inputValue={nameInputValue}
                            onInputChange={(value, action) => {
                                if (action.action !== 'input-blur' && action.action !== 'menu-close') {
                                    setNameInputValue(value);
                                }
                            }}
                            onBlur={() => {
                                if (nameInputValue.trim()) {
                                    onChange({ ...parameter, catalogParameterId: null, parameterName: nameInputValue.trim() });
                                    setNameInputValue('');
                                }
                            }}
                        />
                    </div>
                    <CreateCatalogParameterModal
                        isOpen={showCreateModal}
                        onClose={() => setShowCreateModal(false)}
                        onCreated={(created) => {
                            setShowCreateModal(false);
                            onCatalogParameterCreated(created);
                            onChange({
                                ...parameter,
                                catalogParameterId: created.id,
                                parameterType: (created.parameterType as ParameterSectionParameterType) ?? parameter.parameterType,
                                parameterName: created.name ?? parameter.parameterName,
                                formulaExpression: created.formulaExpression ?? parameter.formulaExpression,
                                unitId: created.unitId ?? parameter.unitId,
                            });
                        }}
                    />
                </Col>
                <Col xs={12} md={12} lg={6} xl={4} xxl={3} xxxl={2}>
                    <div className={styles.control} data-tooltip-id={BODY_TOOLTIP}>
                        <Label space>{t('common.parameters_sections.parameter_type')}</Label>
                        <SelectInput
                            value={parameter.parameterType}
                            options={options}
                            disabled={disabled || !!parameter.catalogParameterId}
                            onChange={(v) => {
                                if (v === ParameterSectionParameterType.WEIGHT) {
                                    onChange({ ...parameter, parameterType: v as ParameterSectionParameterType, unitId: weightUnitId });
                                } else {
                                    onChange({ ...parameter, parameterType: v as ParameterSectionParameterType });
                                }
                                if (valueHasError && v === ParameterSectionParameterType.PRODUCTS.toString()) {
                                    setValueHasError(false);
                                }
                            }}
                            hasError={valueHasError}
                        />
                        {valueHasError && <InputError error={{ type: 'pattern' }} />}
                    </div>
                </Col>
                {!['NUMBER', 'NUMBER_MIN_MAX', 'WEIGHT'].includes(parameter.parameterType ?? '')
                    ? null :
                    <Col xs={12} md={12} lg={6} xl={4} xxl={3} xxxl={1}>
                        <div className={styles.control}>
                            <Label space>{t('common.parameters_sections.unit')}</Label>
                            <SelectInput
                                value={parameter.unitId}
                                options={unitOptions || []}
                                onChange={(v) => onChange({ ...parameter, unitId: v })}
                                disabled={disabled || parameter.parameterType === 'WEIGHT'}
                            />
                        </div>
                    </Col>
                }
                {(parameter.parameterType !== 'PRODUCTS' && parameter.parameterType !== 'NUMBER_MIN_MAX') || family?.isComponent
                    ? null :
                    <Col xs={12} md={12} lg={6} xl={4} xxl={3} xxxl={2}>
                        <div className={styles.control}>
                            <Label space>{t('common.parameters_sections.parameter_family')}</Label>
                            <SelectInput
                                value={parameter.parameterFamilyId}
                                options={selectInputOptions || []}
                                formatOptionLabel={OptionTitleSubTitle}
                                onChange={(v) => onChange({ ...parameter, parameterFamilyId: v })}
                                disabled={disabled}
                            />
                        </div>
                    </Col>
                }
                {['PRICE', 'NUMBER', 'QUANTITY', 'WEIGHT'].includes(parameter.parameterType ?? '') && (!disabled || parameter.formulaExpression) && (
                    <Col xs={12} md={12} lg={6} xl={4} xxl={3} xxxl={2}>
                        <div className={styles.control}>
                            <Label space>{t('common.parameters_sections.formula_expression')}</Label>
                            <TextInput
                                value={ExpressionSystem.formatExpressionForDisplay(
                                    parameter.formulaExpression ?? '',
                                    getFamilyFormulaParameters(allParameters || [], parameter.key, false, t('common.parameters_sections.list_of'), t, catalogParameters)
                                )}
                                onChange={() => {}} // No-op since input is read-only
                                placeholder={t('common.parameters_sections.formula_placeholder')}
                                readOnly={true} // Read-only but without full disabled styling
                                style={{ cursor: 'default' }}
                                expressionButton={{
                                    value: parameter.formulaExpression ?? '',
                                    title: parameter.parameterName ?? t('common.parameters_sections.formula'),
                                    loadParameters: () => getFamilyFormulaParameters(allParameters || [], parameter.key, false, t('common.parameters_sections.list_of'), t, catalogParameters),
                                    onChange: (expr: string | null) => onChange({ ...parameter, formulaExpression: expr }),
                                    disabled: disabled, // The button respects the disabled prop
                                    onValidate: allParameters ? buildFamilyCircularReferenceValidator(allParameters, parameter.key, t) : undefined,
                                }}
                            />
                        </div>
                    </Col>
                )}
                {parameter.parameterType !== ParameterSectionParameterType.PRODUCTS && (
                    <Col xs={12} md={12} lg={6} xl={4} xxl={3} xxxl={1}>
                        <div className={styles.checkbox}>
                            <CheckInput
                                checked={parameter.parameterIsRequired ?? false}
                                disabled={disabled}
                                onChange={(e) => onChange({ ...parameter, parameterIsRequired: e.target.checked })}
                            >
                                {t('common.parameters_sections.parameter_required')}
                            </CheckInput>
                        </div>
                    </Col>
                )}
                {parameter.parameterType === ParameterSectionParameterType.PRODUCTS && (
                    <Col xs={12} md={12} lg={6} xl={4} xxl={3} xxxl={1}>
                        <div className={styles.checkbox}>
                            <CheckInput
                                checked={parameter.requiresTreatment ?? false}
                                disabled={disabled}
                                onChange={(e) => onChange({ ...parameter, requiresTreatment: e.target.checked })}
                            >
                                {t('common.parameters_sections.requires_treatment')}
                            </CheckInput>
                            <InfoIcon onClick={() => setShowTreatmentInfo(true)} />
                            <InformationModal
                                title={t('common.parameters_sections.requires_treatment')}
                                message={t('common.parameters_sections.requires_treatment_tooltip')}
                                onClose={() => setShowTreatmentInfo(false)}
                                isOpen={showTreatmentInfo}
                            />
                        </div>
                    </Col>
                )}
                {parameter.parameterType === ParameterSectionParameterType.PRODUCTS && (
                    <Col xs={12} md={12} lg={6} xl={4} xxl={3} xxxl={1}>
                        <div className={styles.checkbox}>
                            <CheckInput
                                checked={parameter.isEligibleForDiscount ?? false}
                                disabled={disabled}
                                onChange={(e) => onChange({ ...parameter, isEligibleForDiscount: e.target.checked })}
                            >
                                {t('common.parameters_sections.eligible_for_discount')}
                            </CheckInput>
                            <InfoIcon onClick={() => setShowDiscountInfo(true)} />
                            <InformationModal
                                title={t('common.parameters_sections.eligible_for_discount')}
                                message={t('common.parameters_sections.eligible_for_discount_tooltip')}
                                onClose={() => setShowDiscountInfo(false)}
                                isOpen={showDiscountInfo}
                            />
                        </div>
                    </Col>
                )}
            </Row>
        </div>
    );
};

export default forwardRef<HTMLDivElement, ParameterSectionProps>(Parameter);
